import { LOGIN_METHOD, type LoginMethodValue } from '@constants'
import { setLoginOrSignUpMethod } from '@dni-analysis/events/sendLoginEvent'
import { type Liff } from '@line/liff'
import { ButtonIcon } from '@pets-ui/Button/ButtonIcon'
import { HStack } from '@pets-ui/HStack/HStack'
import { Mail } from '@pets-ui/icons'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useThirtyPartyLogin } from '../../../hooks/useThirtyPartyLogin'
import { useContextCosign } from '../../useContextCosign'
import { useContextMyAccountDialog } from '../myAccount/Provider/useContextMyAccountDialog'
import { ButtonThirtyPartyLogin } from './ButtonThirtyPartyLogin'
import { FacebookLogo, GoogleLogo, LineLogo } from './icons'
import { LoginStep } from './types'
import { useContextLogin } from './useContextLogin'

const styles = stylex.create({
  ssoButtons: {
    gap: '12px',
    display: 'flex',
    flexDirection: 'column',
  },
  ssoIconRow: {
    gap: '16px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: '64px',
  },
  iconCircle: {
    borderColor: { default: colors.grey300, ':hover': colors.grey400 },
    borderRadius: '50%',
    borderStyle: 'solid',
    borderWidth: '1px',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    alignItems: 'center',
    backgroundColor: { default: null, ':hover': colors.grey100 },
    cursor: 'pointer',
    display: 'flex',
    fontSize: '28px',
    justifyContent: 'center',
    height: '44px',
    width: '44px',
  },
})

export type ThirtyPartyLoginVariant = 'default' | 'icon'

interface ThirtyPartyLoginProps {
  liff?: Liff
  variant?: ThirtyPartyLoginVariant
  /** 未傳＝全顯示；有傳則僅顯示清單內的方式 */
  allowedMethods?: LoginMethodValue[]
}

export function ThirtyPartyLogin({ liff, variant = 'default', allowedMethods }: ThirtyPartyLoginProps) {
  const isMethodAllowed = (method: LoginMethodValue) => !allowedMethods || allowedMethods.includes(method)
  const { lineLogin, emailLogin, facebookLogin, googleLogin, redirectUrl: thirdPartyLoginRedirectUrl, fallbackUrl: thirdPartyLoginFallbackUrl, tokenLoginUrl } = useContextCosign()
  const { setStep } = useContextLogin()
  const { t } = useTranslation()
  const { showDialogServerError } = useContextMyAccountDialog()
  const { loginByLine, loginByFacebook, loginByGoogle, isMutating } = useThirtyPartyLogin({
    onError: showDialogServerError,
    redirectUrl: thirdPartyLoginRedirectUrl,
    fallbackUrl: thirdPartyLoginFallbackUrl,
    tokenLoginUrl,
    liff,
  })

  const handleClickEmailLogin = () => {
    setStep(LoginStep.LOGIN_EMAIL)
  }

  // OAuth 三方登入會直接導頁離開，不會經過 step 變化，須在點擊當下記下實際 provider。
  // 導頁前已有請求在飛，忽略後續點擊，避免第二個通路覆蓋掉先寫入的 method 造成歸因錯亂
  const handleClickLineLogin = () => {
    if (isMutating) return
    setLoginOrSignUpMethod(LOGIN_METHOD.LINE)
    loginByLine()
  }
  const handleClickFacebookLogin = () => {
    if (isMutating) return
    setLoginOrSignUpMethod(LOGIN_METHOD.FACEBOOK)
    loginByFacebook()
  }
  const handleClickGoogleLogin = () => {
    if (isMutating) return
    setLoginOrSignUpMethod(LOGIN_METHOD.GOOGLE)
    loginByGoogle()
  }

  if (variant === 'icon') {
    const renderIconButton = (key: string, icon: ReactNode, onClick: () => void) => (
      <ButtonIcon key={key} rootStyle={styles.iconCircle} onClick={onClick}>
        {icon}
      </ButtonIcon>
    )
    return (
      <HStack rootStyle={styles.ssoIconRow}>
        {lineLogin && isMethodAllowed(LOGIN_METHOD.LINE) && renderIconButton('line', <LineLogo width={24} height={24} />, handleClickLineLogin)}
        {facebookLogin && isMethodAllowed(LOGIN_METHOD.FACEBOOK) && renderIconButton('facebook', <FacebookLogo width={24} height={24} />, handleClickFacebookLogin)}
        {googleLogin && isMethodAllowed(LOGIN_METHOD.GOOGLE) && renderIconButton('google', <GoogleLogo width={24} height={24} />, handleClickGoogleLogin)}
        {emailLogin && isMethodAllowed(LOGIN_METHOD.EMAIL) && renderIconButton('email', <Mail color={colors.grey600} />, handleClickEmailLogin)}
      </HStack>
    )
  }

  return (
    <div {...stylex.props(styles.ssoButtons)}>
      {lineLogin && isMethodAllowed(LOGIN_METHOD.LINE) && <ButtonThirtyPartyLogin icon={<LineLogo />} onClick={handleClickLineLogin}>{t('login.lineLogin')}</ButtonThirtyPartyLogin>}
      {facebookLogin && isMethodAllowed(LOGIN_METHOD.FACEBOOK) && <ButtonThirtyPartyLogin icon={<FacebookLogo />} onClick={handleClickFacebookLogin}>{t('login.facebookLogin')}</ButtonThirtyPartyLogin>}
      {googleLogin && isMethodAllowed(LOGIN_METHOD.GOOGLE) && <ButtonThirtyPartyLogin icon={<GoogleLogo />} onClick={handleClickGoogleLogin}>{t('login.googleLogin')}</ButtonThirtyPartyLogin>}
      {emailLogin && isMethodAllowed(LOGIN_METHOD.EMAIL) && <ButtonThirtyPartyLogin icon={<Mail />} onClick={handleClickEmailLogin}>{t('login.emailLogin')}</ButtonThirtyPartyLogin>}
    </div>
  )
}
